export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validateZipCode(zip: string): boolean {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
}

export function validatePhone(phone: string): boolean {
  const phoneRegex = /^[\d\s\-\(\)]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, "").length >= 10;
}

export function validateRequired(value: any): boolean {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
}

export function validatePrice(price: number): boolean {
  return typeof price === "number" && price >= 0 && isFinite(price);
}

export function validateInventory(inventory: number): boolean {
  return (
    typeof inventory === "number" &&
    inventory >= 0 &&
    Number.isInteger(inventory)
  );
}

export function validateCategory(category: string): boolean {
  const validCategories = [
    "wind-spinners",
    "glassware",
    "tumblers",
    "in-memory-of",
  ];
  return validCategories.includes(category);
}

export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check if it's an image
  if (!file.type.startsWith("image/")) {
    return { valid: false, error: "File must be an image" };
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return { valid: false, error: "Image must be less than 5MB" };
  }

  // Check file extension
  const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
  const extension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  if (!validExtensions.includes(extension)) {
    return { valid: false, error: "Invalid image format" };
  }

  return { valid: true };
}

export function sanitizeString(str: string): string {
  return str.trim().replace(/[<>]/g, "");
}

export interface ValidationErrors {
  [key: string]: string;
}

export function validateProductData(data: any): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!validateRequired(data.name)) {
    errors.name = "Product name is required";
  }

  if (!validateRequired(data.price) || !validatePrice(Number(data.price))) {
    errors.price = "Valid price is required";
  }

  if (!validateRequired(data.category) || !validateCategory(data.category)) {
    errors.category = "Valid category is required";
  }

  if (
    data.inventory !== undefined &&
    !validateInventory(Number(data.inventory))
  ) {
    errors.inventory = "Inventory must be a non-negative integer";
  }

  // Optional: Validate description length (max 10000 characters)
  if (data.description && typeof data.description === "string") {
    const maxLength = 10000;
    if (data.description.length > maxLength) {
      errors.description = `Description must be less than ${maxLength} characters`;
    }
  }

  return errors;
}

export function validateOrderData(data: any): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!validateRequired(data.customer_name)) {
    errors.customer_name = "Customer name is required";
  }

  if (!validateRequired(data.customer_email) || !validateEmail(data.customer_email)) {
    errors.customer_email = "Valid email is required";
  }

  if (data.customer_phone && !validatePhone(data.customer_phone)) {
    errors.customer_phone = "Invalid phone number";
  }

  if (!validateRequired(data.shipping_address)) {
    errors.shipping_address = "Shipping address is required";
  }

  if (!validateRequired(data.shipping_city)) {
    errors.shipping_city = "City is required";
  }

  if (!validateRequired(data.shipping_state)) {
    errors.shipping_state = "State is required";
  }

  if (!validateRequired(data.shipping_zip) || !validateZipCode(data.shipping_zip)) {
    errors.shipping_zip = "Valid zip code is required";
  }

  return errors;
}

/**
 * Sanitizes contact message text to prevent XSS attacks while preserving newlines.
 * - Strips all HTML tags
 * - Escapes special characters: <, >, &, ", '
 * - Preserves newlines (\n, \r\n)
 * - Trims excessive whitespace
 * - Enforces maximum length
 */
export function sanitizeContactMessage(message: string): string {
  if (!message || typeof message !== "string") {
    return "";
  }

  // Step 1: Trim whitespace
  let sanitized = message.trim();

  // Step 2: Enforce maximum length (10,000 characters)
  const maxLength = 10000;
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // Step 3: Strip HTML tags (comprehensive list of dangerous tags)
  // This removes opening and closing tags along with their content for dangerous tags
  const dangerousTags = [
    'script', 'iframe', 'object', 'embed', 'link', 'style',
    'meta', 'base', 'form', 'input', 'button', 'select', 'textarea'
  ];

  dangerousTags.forEach(tag => {
    // Remove opening and closing tags with content
    const regex = new RegExp(`<${tag}[^>]*>.*?</${tag}>`, 'gis');
    sanitized = sanitized.replace(regex, '');
    // Remove self-closing tags
    const selfClosingRegex = new RegExp(`<${tag}[^>]*/>`, 'gi');
    sanitized = sanitized.replace(selfClosingRegex, '');
  });

  // Step 4: Remove all remaining HTML tags (but preserve text content)
  sanitized = sanitized.replace(/<[^>]+>/g, '');

  // Step 5: Escape special HTML characters
  sanitized = sanitized
    .replace(/&/g, '&amp;')   // Must be first to avoid double-escaping
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;'); // Extra safety for forward slashes

  // Step 6: Preserve newlines (they are already preserved, no changes needed)

  // Step 7: Remove excessive consecutive whitespace (but preserve single newlines)
  // Replace multiple spaces with single space
  sanitized = sanitized.replace(/ {2,}/g, ' ');
  // Replace more than 2 consecutive newlines with 2 newlines
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n');

  return sanitized;
}

/**
 * Validates contact message content
 * Returns null if valid, or an error message string if invalid
 */
export function validateContactMessage(message: string): string | null {
  if (!message || typeof message !== "string") {
    return "Message is required";
  }

  const trimmed = message.trim();

  // Check minimum length (at least 10 characters)
  if (trimmed.length < 10) {
    return "Message must be at least 10 characters long";
  }

  // Check maximum length (10,000 characters)
  if (trimmed.length > 10000) {
    return "Message must be less than 10,000 characters";
  }

  // Check if message is only whitespace (spaces, tabs, newlines)
  if (!/\S/.test(trimmed)) {
    return "Message cannot contain only whitespace";
  }

  return null; // Valid
}

/**
 * Sanitizes name fields (first name, last name) to prevent XSS
 * - Removes HTML tags
 * - Escapes special characters
 * - Trims whitespace
 * - Enforces maximum length (50 characters)
 * - Allows letters, spaces, hyphens, apostrophes (for names like O'Brien, Mary-Jane)
 */
export function sanitizeName(name: string): string {
  if (!name || typeof name !== "string") {
    return "";
  }

  // Trim and enforce max length
  let sanitized = name.trim().substring(0, 50);

  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]+>/g, '');

  // Escape special HTML characters
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');

  // Remove any characters that aren't letters, spaces, hyphens, or apostrophes (after escaping)
  // This allows names like "O'Brien", "Mary-Jane", "José"
  sanitized = sanitized.replace(/[^a-zA-ZÀ-ÿ\s\-'&#;]/g, '');

  return sanitized.trim();
}

/**
 * Validates name fields
 * Returns null if valid, or an error message string if invalid
 */
export function validateName(name: string, fieldName: string = "Name"): string | null {
  if (!name || typeof name !== "string") {
    return `${fieldName} is required`;
  }

  const trimmed = name.trim();

  if (trimmed.length < 1) {
    return `${fieldName} is required`;
  }

  if (trimmed.length > 50) {
    return `${fieldName} must be less than 50 characters`;
  }

  // Check for valid name characters (letters, spaces, hyphens, apostrophes)
  if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmed)) {
    return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
  }

  return null;
}

/**
 * Sanitizes phone numbers
 * - Removes HTML tags
 * - Strips all non-digit characters except +, -, (, ), and spaces
 * - Enforces maximum length
 */
export function sanitizePhone(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }

  // Trim and enforce max length (20 characters for international numbers)
  let sanitized = phone.trim().substring(0, 20);

  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]+>/g, '');

  // Only allow digits, +, -, (, ), and spaces
  sanitized = sanitized.replace(/[^0-9+\-() ]/g, '');

  return sanitized.trim();
}
